import { useMemo, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { ROUTES, PERMISSIONS } from "@/constants";
import { useAuthContext } from "@/context/AuthContext";
import { filterNavItems } from "@/utils/navAccess";
import { cn } from "@/lib/utils";
import {
  APP_SIDEBAR_ASIDE_CLASS,
  APP_SIDEBAR_HEADER_ROW_CLASS,
  APP_SIDEBAR_TOGGLE_BUTTON_CLASS,
  appSidebarNavClassName,
  APP_SIDEBAR_SECTION_LABEL_CLASS,
  appSidebarNavLinkClassName,
} from "@/lib/sidebarNav";
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  ListTodo,
  ChevronsUp,
  BookOpen,
  MapPin,
  FileDown,
  Network,
  Gauge,
  ClipboardCheck,
} from "lucide-react";
import {
  TripleChevronCloseIcon,
  TripleChevronOpenIcon,
} from "@/components/layout/PortalSidebarToggle";
import { useIsMobile } from "@/hooks/useIsMobile";
import { ExecutiveAppHeader } from "@/components/layout/ExecutiveAppHeader";
import {
  DashboardHeaderSlotProvider,
  DashboardHeaderSlot,
} from "@/context/DashboardHeaderSlotContext";
import { projectManagerService } from "@/features/projectManager/services/projectManagerService";
import { ChatProvider } from "@/features/chat/context/ChatContext";
import { AiAssistantProvider } from "@/features/aiAssistant/context/AiAssistantContext";
import { AiAssistantNavItem } from "@/features/aiAssistant/components/AiAssistantNavItem";

export function ProjectManagerLayout() {
  const { user } = useAuthContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const outletCtx = {
    projectService: projectManagerService,
    basePath: ROUTES.PROJECT_MANAGER,
    skipProjectPicker: true,
  };

  const tabs = useMemo(
    () => [
      {
        label: "Dashboard",
        path: ROUTES.PROJECT_MANAGER,
        end: true,
        icon: LayoutDashboard,
        requiredPermissions: [PERMISSIONS.VIEW_PROJECT, PERMISSIONS.VIEW_TASK],
      },
      {
        label: "Project Management",
        path: `${ROUTES.PROJECT_MANAGER}/projects`,
        icon: FolderKanban,
        requiredPermissions: [PERMISSIONS.VIEW_PROJECT, PERMISSIONS.VIEW_TASK],
      },
      {
        label: "Task Management",
        path: `${ROUTES.PROJECT_MANAGER}/tasks`,
        icon: ListTodo,
        requiredPermissions: [PERMISSIONS.MANAGE_TASK],
      },
      {
        label: "Location Management",
        path: `${ROUTES.PROJECT_MANAGER}/locations`,
        icon: MapPin,
        requiredPermissions: [PERMISSIONS.VIEW_PROJECT, PERMISSIONS.MANAGE_PROJECT],
      },
      {
        label: "Operational Indicators",
        path: ROUTES.PROJECT_MANAGER_OPERATIONAL_INDICATORS,
        icon: Gauge,
        requiredPermissions: [PERMISSIONS.MANAGE_PROJECT],
      },
      {
        label: "Registration Confirmation",
        path: ROUTES.PROJECT_MANAGER_REGISTRATION_CONFIRMATION,
        icon: ClipboardCheck,
        requiredPermissions: [PERMISSIONS.MANAGE_PROJECT],
      },
      {
        label: "Escalation History",
        path: ROUTES.PROJECT_MANAGER_ESCALATION_HISTORY,
        icon: ChevronsUp,
        requiredPermissions: [PERMISSIONS.VIEW_PROJECT, PERMISSIONS.VIEW_TASK],
      },
      {
          label: 'Structure',
          path: ROUTES.PROJECT_MANAGER_STRUCTURE,
          icon: Network,
          // No permission or role gate: every account may see the structure.
          // Where the tree is rooted per role is decided by the server.
      },
      {
          label: 'Export',
          path: ROUTES.PROJECT_MANAGER_EXPORT,
          icon: FileDown,
          // No permission or role gate: every account may export. What differs
          // per role is the scope, which the server derives from the account.
      },
      {
        label: "User Manual",
        path: ROUTES.PROJECT_MANAGER_HELP,
        icon: BookOpen,
        requiredPermissions: [PERMISSIONS.VIEW_PROJECT],
      },
    ],
    [],
  );

  const visibleTabs = useMemo(() => filterNavItems(user, tabs), [user, tabs]);

  return (
    <DashboardHeaderSlotProvider>
      <ChatProvider>
          <AiAssistantProvider>
        <div className="flex h-screen h-dvh flex-col gap-3 overflow-hidden bg-background p-3">
          <ExecutiveAppHeader
            title="Project Management Dashboard"
            center={<DashboardHeaderSlot />}
            onOpenSidebar={() => setSidebarOpen(true)}
          />

          <div className="relative flex min-h-0 flex-1 overflow-hidden">
            {isMobile && sidebarOpen ? (
              <div
                className="fixed inset-0 z-30 bg-black/40 md:hidden"
                onClick={() => setSidebarOpen(false)}
                aria-hidden
              />
            ) : null}
            <aside
              className={cn(
                APP_SIDEBAR_ASIDE_CLASS,
                isMobile || sidebarOpen ? "w-64" : "w-14",
                isMobile && !sidebarOpen ? "-translate-x-full" : "translate-x-0",
              )}
            >
              <div className={APP_SIDEBAR_HEADER_ROW_CLASS}>
                {sidebarOpen ? (
                  <div className="flex min-w-[16rem] w-full justify-end px-2 py-2">
                    <button
                      type="button"
                      onClick={() => setSidebarOpen(false)}
                      className={APP_SIDEBAR_TOGGLE_BUTTON_CLASS}
                      aria-label="Collapse sidebar to icons only"
                      title="Show icons only"
                    >
                      <TripleChevronCloseIcon />
                    </button>
                  </div>
                ) : (
                  <div className="flex w-full justify-center py-2">
                    <button
                      type="button"
                      onClick={() => setSidebarOpen(true)}
                      className={APP_SIDEBAR_TOGGLE_BUTTON_CLASS}
                      aria-label="Expand sidebar"
                      title="Expand sidebar"
                    >
                      <TripleChevronOpenIcon />
                    </button>
                  </div>
                )}
              </div>
              <nav className={appSidebarNavClassName(sidebarOpen)}>
                {sidebarOpen ? (
                  <p className={APP_SIDEBAR_SECTION_LABEL_CLASS}>
                    Navigation
                  </p>
                ) : null}
                {visibleTabs.map(({ requiredPermissions: _rp, requiredRole: _rr, ...tab }) => (
                  <NavLink
                    key={tab.path}
                    to={tab.path}
                    end={tab.end ?? false}
                    title={tab.label}
                    onClick={() => isMobile && setSidebarOpen(false)}
                    className={({ isActive }) =>
                      appSidebarNavLinkClassName({ isActive, expanded: sidebarOpen })
                    }
                  >
                    <tab.icon className="h-5 w-5 flex-shrink-0" aria-hidden />
                    <span className={cn("leading-tight", !sidebarOpen && "sr-only")}>
                      {tab.label}
                    </span>
                  </NavLink>
                ))}
                  <AiAssistantNavItem
                      expanded={sidebarOpen}
                      onActivate={() => isMobile && setSidebarOpen(false)}
                  />
              </nav>
            </aside>

            <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-0.5 md:p-3">
              <Outlet context={outletCtx} />
            </main>
          </div>
        </div>
          </AiAssistantProvider>
      </ChatProvider>
    </DashboardHeaderSlotProvider>
  );
}

export default ProjectManagerLayout;
